/**
 * GuidedEditorProgress - Progress indicator for multi-step guided editor
 *
 * Shows step number, completion status, and connector lines between steps.
 *
 * Epic: MVP 1.20.6-S - Key Locations Guided Editor with GPX Intelligence
 * Story: SCRUM-233 (US-1.20.6-S.1-01: Progress Indicator Component)
 *
 * @component
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * Step status indicator with connection lines
 */
const GuidedEditorProgress = ({ steps, currentStep }) => {
  return (
    <div className="w-full py-6">
      <div className="flex items-center justify-between max-w-3xl mx-auto">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const isCurrent = currentStep === stepNumber;
          const isCompleted = currentStep > stepNumber;
          const isUpcoming = currentStep < stepNumber;

          return (
            <React.Fragment key={step.id}>
              {/* Step Circle */}
              <div className="flex flex-col items-center relative">
                {/* Circle */}
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                    transition-all duration-200
                    ${
                      isCompleted
                        ? 'bg-green-600 text-white'
                        : isCurrent
                        ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                        : 'bg-gray-200 text-gray-500'
                    }
                  `}
                >
                  {isCompleted ? (
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    stepNumber
                  )}
                </div>

                {/* Label */}
                <div
                  className={`
                    mt-2 text-xs font-medium text-center max-w-[100px]
                    ${isCurrent ? 'text-blue-700' : isCompleted ? 'text-green-700' : 'text-gray-500'}
                  `}
                >
                  {step.label}
                </div>

                {/* Optional status badge */}
                {step.optional && isUpcoming && (
                  <div className="mt-1 text-xs text-gray-400 italic">
                    Optional
                  </div>
                )}
              </div>

              {/* Connector Line (not after last step) */}
              {index < steps.length - 1 && (
                <div className="flex-1 h-1 mx-2 relative" style={{ minWidth: '40px' }}>
                  <div
                    className={`
                      absolute inset-0 rounded-full transition-all duration-300
                      ${isCompleted ? 'bg-green-600' : 'bg-gray-200'}
                    `}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

GuidedEditorProgress.propTypes = {
  /**
   * Array of step objects
   * Each step should have: { id, label, optional? }
   */
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      optional: PropTypes.bool
    })
  ).isRequired,

  /**
   * Current step number (1-indexed)
   */
  currentStep: PropTypes.number.isRequired
};

export default GuidedEditorProgress;
